import { Typography } from '@mui/material';
import { BudgetBys, BudgetExpenseOrIncome } from 'business/constants';
import { ScopeT, UserScopesT } from 'business/definitions';
import Loading from 'components/Loading';
import React from 'react';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import { usePrimaryColor } from 'styles/theme';
import Grid from './Grid';
import { getColumns } from './helpers/gridHelpers';
import { useGetOperationsAmounts } from './hooks/useGetOperationsAmounts';
import { useTranslation } from 'react-i18next';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import Flex from 'ui/Flex/Flex';
import ConditionnalZoomIconButton from 'components/ConditionnalZoomIconButton';
import { FALLBACK_SECONDARY_BUDGET_AXIS_DISPLAY } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableProvider';
import { translateBlock } from 'i18n';
import { pluralize } from 'helpers/formatHelper';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';

type Props = {
    allBudgetProposals: BudgetProposalT[];
    inscriptionsData: InscriptionT[];
    areInscriptionsLoading: boolean;
    referenceYear: number;
    budgetBy: BudgetBys;
    expenseOrIncome: BudgetExpenseOrIncome;
    userScopes: UserScopesT;
    scopes: ScopeT[];
    hideEmptyOperations: boolean;
    budgetName: string;
};

export default function FinancesSoftwareOperations({
    allBudgetProposals,
    inscriptionsData,
    areInscriptionsLoading,
    referenceYear,
    budgetBy,
    expenseOrIncome,
    userScopes,
    scopes,
    hideEmptyOperations,
    budgetName,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const { data: operationsAmounts, isLoading: operationsAmountsIsLoading } =
        useGetOperationsAmounts({
            params: {
                exercice: referenceYear,
                expenseIncome: expenseOrIncome,
                budgetName,
            },
        });

    const isLoading = areInscriptionsLoading || operationsAmountsIsLoading;

    const columns = getColumns(referenceYear, usePrimaryColor(), t);
    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    if (isLoading) {
        return <Loading />;
    }
    const sourceTableLabel = sourceTable
        ? translateBlock(sourceTable)
        : FALLBACK_SECONDARY_BUDGET_AXIS_DISPLAY;
    return (
        <>
            <Flex gap="1rem" alignItems="center" mb={2}>
                <ConditionnalZoomIconButton />
                <Typography variant="h5">
                    {t('budgetOverview.financeSecondaryBudgetAxisElement', {
                        sourceTable: pluralize(sourceTableLabel),
                    })}
                </Typography>
            </Flex>
            <Grid
                hideEmptyOperations={hideEmptyOperations}
                inscriptionsByOperation={inscriptionsData}
                operationsAmounts={operationsAmounts}
                columnsOperations={columns}
                allBudgetProposals={allBudgetProposals}
                budgetBy={budgetBy!}
                userScopes={userScopes}
                scopes={scopes}
                expenseOrIncome={expenseOrIncome}
            />
        </>
    );
}
